import { useState } from "react";
import { endpoints } from "../api/endpoints";
import { unsafeLinkProblem } from "../api/problem";
import { useFireContext } from "../context";
import { formatDateTime } from "../format/datetime";
import { ProblemAlert } from "./ProblemAlert";

/**
 * Only https links from the FER API are followed or shown: download links, checkout pages and the incident's maps
 * link (http only for localhost, and only in development builds). Anything else answers null.
 */
export function safeHttpsUrl(href: unknown): string | null {
  if (typeof href !== "string") return null;
  try {
    const u = new URL(href);
    const local = import.meta.env.DEV && (u.hostname === "localhost" || u.hostname === "127.0.0.1");
    return u.protocol === "https:" || (local && u.protocol === "http:") ? u.href : null;
  } catch {
    return null;
  }
}

/** A `geo:` URI (navigation apps) is shown as text only when it is a plain `geo:lat,lon` form. */
export function safeGeoUri(uri: unknown): string | null {
  return typeof uri === "string" && /^geo:-?\d+(\.\d+)?,-?\d+(\.\d+)?$/.test(uri) ? uri : null;
}

/**
 * A file attached to a FER record (alert, report or inspection photo). Files are read only through FER's scoped
 * `getFileDownload`: the link is fetched when asked for, checked (https only), and shown with its expiry.
 */
export function FileLink({ fileId, label }: { fileId: string; label: string }) {
  const { api } = useFireContext();
  const [state, setState] = useState<{ busy: boolean; url: string | null; expires: string | null; error: unknown }>({
    busy: false,
    url: null,
    expires: null,
    error: null,
  });
  const open = async () => {
    setState({ busy: true, url: null, expires: null, error: null });
    try {
      const d = await endpoints.getFileDownload(api, fileId);
      const url = safeHttpsUrl(d?.download_url);
      if (!url) throw unsafeLinkProblem();
      setState({ busy: false, url, expires: d.expires_at ?? null, error: null });
    } catch (e) {
      setState({ busy: false, url: null, expires: null, error: e });
    }
  };
  return (
    <span className="fer-file">
      {state.url ? (
        <a href={state.url} target="_blank" rel="noopener noreferrer">
          Open {label}
          {state.expires ? ` (link valid until ${formatDateTime(state.expires)})` : ""}
        </a>
      ) : (
        <button type="button" className="fer-link" onClick={() => void open()} disabled={state.busy}>
          {state.busy ? `Getting ${label}…` : `Get ${label}`}
        </button>
      )}
      <ProblemAlert error={state.error} />
    </span>
  );
}

/** A list of file ids as download buttons ("photo 1", "photo 2", …). */
export function FileLinks({ ids, noun = "Photo" }: { ids: unknown; noun?: string }) {
  const list = Array.isArray(ids) ? ids.filter((x): x is string => typeof x === "string" && x.length > 0) : [];
  if (list.length === 0) return <span className="fer-muted">None</span>;
  return (
    <ul className="fer-files" aria-label={`${noun}s`}>
      {list.map((fid, i) => (
        <li key={fid}>
          <FileLink fileId={fid} label={`${noun.toLowerCase()} ${i + 1}`} />
        </li>
      ))}
    </ul>
  );
}
